import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import {
  UserGroupIcon,
  TruckIcon,
  CalendarIcon,
  ChartBarIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Statistics = () => {
  const { get } = useApi();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('LAST_MONTH');
  const [activeTab, setActiveTab] = useState('drivers');
  const [driverStats, setDriverStats] = useState([]);
  const [vehicleStats, setVehicleStats] = useState([]);

  const periods = [
    { value: 'LAST_MONTH', label: 'Last Month' },
    { value: 'LAST_3_MONTHS', label: 'Last 3 Months' },
    { value: 'LAST_6_MONTHS', label: 'Last 6 Months' }
  ];

  const isAdmin = currentUser?.roles?.includes('ADMIN') || false;

  useEffect(() => {
    if (isAdmin) {
      fetchStatistics();
    }
  }, [period, activeTab]);

  const fetchStatistics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (activeTab === 'drivers') {
        const response = await get(`/api/statistics/drivers?period=${period}`);
        if (response.data) {
          setDriverStats(response.data);
        }
      } else {
        const response = await get(`/api/statistics/vehicles?period=${period}`);
        if (response.data) {
          setVehicleStats(response.data);
        }
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
  };

  const getPeriodLabel = () => {
    return periods.find(p => p.value === period)?.label || 'Last Month';
  };

  // Chart color palette
  const colors = [
    '#4F46E5', '#7C3AED', '#EC4899', '#EF4444', '#F59E0B',
    '#10B981', '#3B82F6', '#8B5CF6', '#F472B6', '#F97316'
  ];

  // Prepare chart data for a single entity
  const prepareEventTypeChartData = (entity) => {
    if (!entity?.eventTypeCounts || entity.eventTypeCounts.length === 0) {
      return null;
    }

    const labels = entity.eventTypeCounts.map(e => e.eventTypeDescription || 'Unknown');
    const data = entity.eventTypeCounts.map(e => e.count);

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors.slice(0, data.length),
          borderWidth: 2,
          borderColor: '#ffffff',
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: { size: 10 },
          padding: 10,
          boxWidth: 12,
        }
      }
    },
    cutout: '60%',
  };

  // Render driver card
  const renderDriverCard = (driver) => {
    const chartData = prepareEventTypeChartData(driver);
    
    return (
      <div key={driver.driverId} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {driver.driverName || `Driver #${driver.driverId}`}
            </h3>
            {driver.driverMobile && (
              <p className="text-sm text-gray-500 dark:text-gray-400">📱 {driver.driverMobile}</p>
            )}
            {driver.driverEmail && (
              <p className="text-sm text-gray-500 dark:text-gray-400">✉️ {driver.driverEmail}</p>
            )}
          </div>
          <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">
            ID: {driver.driverId}
          </span>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400">{driver.totalEvents || 0}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Events</p>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <p className="text-xl font-bold text-green-600 dark:text-green-400">{driver.totalTrips || 0}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Trips</p>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <p className="text-xl font-bold text-yellow-600 dark:text-yellow-400">{driver.totalActiveTrips || 0}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Active Trips</p>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{driver.totalCompletedTrips || 0}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Completed</p>
          </div>
        </div>

        {/* Chart */}
        {chartData && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Event Types Distribution</h4>
            <div className="h-40">
              <Doughnut data={chartData} options={chartOptions} />
            </div>
          </div>
        )}

        {/* Period Summary */}
        {driver.periodStats && (
          <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {getPeriodLabel()}: <span className="font-semibold text-indigo-600 dark:text-indigo-400">{driver.periodStats.totalEvents || 0} events</span>
            </p>
          </div>
        )}
      </div>
    );
  };

  // Render vehicle card
  const renderVehicleCard = (vehicle) => {
    const chartData = prepareEventTypeChartData(vehicle);
    
    return (
      <div key={vehicle.vehicleId} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {vehicle.vehicleBrand || 'Unknown Brand'} 
              {vehicle.vehiclePlates && <span className="text-sm text-gray-500 ml-2">({vehicle.vehiclePlates})</span>}
            </h3>
            {vehicle.vehicleColor && (
              <p className="text-sm text-gray-500 dark:text-gray-400">🎨 Color: {vehicle.vehicleColor}</p>
            )}
          </div>
          <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-medium">
            ID: {vehicle.vehicleId}
          </span>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400">{vehicle.totalEvents || 0}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Events</p>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <p className="text-xl font-bold text-green-600 dark:text-green-400">{vehicle.totalTrips || 0}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Trips</p>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <p className="text-xl font-bold text-yellow-600 dark:text-yellow-400">{vehicle.totalActiveTrips || 0}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Active Trips</p>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{vehicle.totalCompletedTrips || 0}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Completed</p>
          </div>
        </div>

        {/* Chart */}
        {chartData && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Event Types Distribution</h4>
            <div className="h-40">
              <Doughnut data={chartData} options={chartOptions} />
            </div>
          </div>
        )}

        {/* Period Summary */}
        {vehicle.periodStats && (
          <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {getPeriodLabel()}: <span className="font-semibold text-indigo-600 dark:text-indigo-400">{vehicle.periodStats.totalEvents || 0} events</span>
            </p>
          </div>
        )}
      </div>
    );
  };

  // Summary cards
  const renderSummaryCards = () => {
    const data = activeTab === 'drivers' ? driverStats : vehicleStats;
    if (!data || data.length === 0) return null;

    const totalEvents = data.reduce((sum, item) => sum + (item.totalEvents || 0), 0);
    const totalTrips = data.reduce((sum, item) => sum + (item.totalTrips || 0), 0);
    const totalActive = data.reduce((sum, item) => sum + (item.totalActiveTrips || 0), 0);
    const totalCompleted = data.reduce((sum, item) => sum + (item.totalCompletedTrips || 0), 0);

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-4 text-white shadow-lg">
          <p className="text-xs opacity-80">Total Events</p>
          <p className="text-2xl font-bold">{totalEvents}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white shadow-lg">
          <p className="text-xs opacity-80">Total Trips</p>
          <p className="text-2xl font-bold">{totalTrips}</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-4 text-white shadow-lg">
          <p className="text-xs opacity-80">Active Trips</p>
          <p className="text-2xl font-bold">{totalActive}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg">
          <p className="text-xs opacity-80">Completed Trips</p>
          <p className="text-2xl font-bold">{totalCompleted}</p>
        </div>
      </div>
    );
  };

  // Period selector
  const renderPeriodSelector = () => (
    <div className="flex flex-wrap items-center gap-2">
      <CalendarIcon className="h-5 w-5 text-gray-400" />
      {periods.map((p) => (
        <button
          key={p.value}
          onClick={() => handlePeriodChange(p.value)}
          className={`px-3 py-1.5 text-sm rounded-lg transition-all duration-200 ${
            period === p.value
              ? 'bg-indigo-600 text-white shadow-md'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          {p.label}
        </button>
      ))}
      <button
        onClick={fetchStatistics}
        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
        title="Refresh"
      >
        <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-8 text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-yellow-500" />
          <h2 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">Access Denied</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            You don't have permission to view statistics.
          </p>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;
  
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          <p className="font-medium">Error loading statistics</p>
          <p className="text-sm">{error}</p>
          <button
            onClick={fetchStatistics}
            className="mt-2 px-4 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const data = activeTab === 'drivers' ? driverStats : vehicleStats;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <ChartBarIcon className="h-7 w-7 text-indigo-600" />
            <span>Statistics Dashboard</span>
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Event statistics for drivers and vehicles
          </p>
        </div>
        {renderPeriodSelector()}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('drivers')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
              activeTab === 'drivers'
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <UserGroupIcon className="h-5 w-5" />
            <span>Drivers ({driverStats.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('vehicles')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
              activeTab === 'vehicles'
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <TruckIcon className="h-5 w-5" />
            <span>Vehicles ({vehicleStats.length})</span>
          </button>
        </nav>
      </div>

      {/* Summary Cards */}
      {renderSummaryCards()}

      {/* Results */}
      {data && data.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {activeTab === 'drivers'
            ? data.map((item) => renderDriverCard(item))
            : data.map((item) => renderVehicleCard(item))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
          <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No data available</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            No {activeTab} found for the selected period.
          </p>
        </div>
      )}
    </div>
  );
};

export default Statistics;